import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageLayout } from '@xtnd/web/shared/layout';
import { TabNavigation, type Tab } from '@xtnd/web/shared/components';
import { useReportFeatureEnabled } from '../../../hooks';
import { REPORTS_ROUTES } from '@xtnd/web/shared/constants';

interface TrustReconciliationLayoutProps {
  children: ReactNode;
}

export function TrustReconciliationLayout({ children }: TrustReconciliationLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading, error, refetch, isReportingEnabled, isTrustReconciliationEnabled } =
    useReportFeatureEnabled();

  const isDisabled = !isReportingEnabled || !isTrustReconciliationEnabled;

  // Determine active tab from URL path
  const getActiveTab = (): 'dashboard' | 'history' => {
    if (location.pathname.includes('/history')) {
      return 'history';
    }
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleTabChange = (tabId: string) => {
    if (tabId === 'history') {
      navigate(REPORTS_ROUTES.TRUST.HISTORY, { replace: true });
    } else {
      navigate(`${REPORTS_ROUTES.TRUST.DASHBOARD}?tab=by-client`, { replace: true });
    }
  };

  const tabs: Tab[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'HiChartBar',
    },
    {
      id: 'history',
      label: 'History',
      icon: 'HiClock',
    },
  ];

  return (
    <PageLayout
      title="3-Way Trust Reconciliation Report"
      description="Verifies Clio Trust transactions are accurately synced to QuickBooks Online Trust Bank and Trust Liability accounts"
      icon="HiShieldCheck"
      isLoading={isLoading}
      isDisabled={isDisabled}
      isError={!!error}
      errorState={{
        title: 'Failed to Load Report Configuration',
        description:
          error?.message ?? 'An unknown error occurred while loading the report configuration.',
        onRetry: () => refetch(),
      }}
      disabledState={{
        title: 'Trust Reconciliation Report Disabled',
        description:
          'The Trust Reconciliation Report is not enabled for this tenant. Please contact your administrator to enable this reporting feature.',
      }}
      skeletonProps={{
        summaryCardCount: 3,
        tableRows: 10,
        tableColumns: 6,
      }}
    >
      <div className="space-y-6">
        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="mt-6">{children}</div>
      </div>
    </PageLayout>
  );
}
